import Joi from "joi";
import { useEffect, useMemo, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getTaskRespects,
  getTaskStatus,
  getDepartments,
} from "../../../../../action/common/commonActionCreator";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import {
  ICreateTask,
  ITaskCreateData,
} from "../../../../../models/master/task";
import { createTask } from "../../../../../action/masters/taskActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import { TaskRespects } from "../../../../../models/dashboard/adminDashboard";
import LoaderSpinner from "../../../../../common-components/loader";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import moment from "moment";
import { IDropdownData } from "../../../../../models/ICommon";

const TaskCreate = (props: ICreateTask) => {
  const {
    createTask,
    setShowCreate,
    getDepartments,
    departments,
    taskRespects,
    getTaskRespects,
    getTaskStatus,
    taskStatus,
    getAllTasks,
  } = props;

  const initialData: ITaskCreateData = {
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    departmentId: null,
    taskRespect: 0,
    taskCurrentStatus: 0,
  };

  // states

  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [allTaskStatus, setAllTaskStatus] = useState<IDropdownData[]>([]);

  // change the data at the time onchange fields
  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    let _data = { ...data, [event.target.name]: event.target.value };

    errors && delete errors[event.target.name];
    setData(_data);
  };

  const schema = Joi.object().keys({
    name: Joi.string().required().label("Name is required"),
    description: Joi.string().required().label("Description is required"),
    startDate: Joi.string().required().label("Start Date is required"),
    endDate: Joi.string().required().label("End Date is required"),
    taskRespect: Joi.number()
      .greater(0)
      .required()
      .label("Task Respect is required"),
    taskCurrentStatus: Joi.number()
      .greater(0)
      .required()
      .label("Task Status is required"),
    departmentId: Joi.when("taskRespect", {
      is: TaskRespects.Department,
      then: Joi.number().required().label("Department is required"),
      otherwise: Joi.number().allow(null),
    }),
  });

  const onSave = async () => {
    const validationErrors = validateFields(data, schema);
    if (validationErrors === undefined) {
      try {
        setLoading(true);
        await createTask(data, setAlert);
      } finally {
        setLoading(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  // setting sweet alert dynamically
  const setAlert = (type: string, name?: string) => {
    let alertData = getAlertData(type, name);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllTasks();
      setShowCreate(false);
    }
  };

  useEffect(() => {
    getDepartments();
    getTaskRespects();
    getTaskStatus();
    getAllTasks();
  }, []);

  useEffect(() => {
    if (taskStatus?.length > 0) {
      setAllTaskStatus(taskStatus);
    }
  }, [taskStatus]);

  const onChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    errors && delete errors[name];
    const updatedData = { ...data, [name]: value };

    // Validate dates
    if (name === "endDate" && value < updatedData.startDate) {
      setErrors({
        ...errors,
        endDate: "End date cannot be earlier than the start date.",
      });
    } else if (
      name === "startDate" &&
      updatedData.endDate &&
      updatedData.endDate < value
    ) {
      setErrors({ ...errors, endDate: "Please adjust the end date." });
      updatedData.endDate = "";
    }

    if (name === "startDate" && moment(value).isAfter(moment(), "day")) {
      updatedData.taskCurrentStatus = 0;
    }
    setData(updatedData);
  };

  const isStartDateInFuture = () => {
    return moment(data.startDate).isAfter(moment(), "day");
  };

  const getFilteredTaskStatus = useMemo(() => {
    if (isStartDateInFuture()) {
      return allTaskStatus.filter(
        (status: any) =>
          status.name === "Open" ||
          status.name === "Hold"
      );
    }
    return allTaskStatus;
  }, [taskStatus, isStartDateInFuture])

  return (
    <>
      <div className="modal-body">
        <h2>Create Task</h2>
        <div className="row mt-3">
          <div className="col-md-12 form-group">
            <FloatingLabelTextInput
              label={"Task Name "}
              onChange={onChange}
              isMandatory={true}
              value={data.name}
              name={"name"}
              maxLength={70}
            />
            {errors?.name && <div className="text-danger">{errors.name}</div>}
          </div>
          <div className="col-md-12 form-group">
            <label className="profile_details_text">
              Task Description <i className="label-asterisk"> * </i>
            </label>
            <textarea
              name="description"
              value={data.description}
              onChange={onChange}
              maxLength={1000}
            ></textarea>
            {errors?.description && (
              <div className="text-danger">{errors.description}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">
              Task Start Date <i className="label-asterisk"> * </i>
            </label>
            <input
              type="date"
              name="startDate"
              className="form-control"
              onChange={onChangeDate}
              value={data.startDate}
              min={new Date().toISOString().split("T")[0]}
            />
            {errors?.startDate && (
              <div className="text-danger">{errors.startDate}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <label className="profile_details_text">
              Task End Date <i className="label-asterisk"> * </i>
            </label>
            <input
              type="date"
              name="endDate"
              className="form-control"
              onChange={onChangeDate}
              value={data.endDate}
              min={data.startDate}
            />
            {errors?.endDate && (
              <div className="text-danger">{errors.endDate}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"Task Current Status "}
              onChange={onChange}
              isMandatory={true}
              value={data.taskCurrentStatus}
              name={"taskCurrentStatus"}
              optionsList={getFilteredTaskStatus || []}
            />
            {errors?.taskCurrentStatus && (
              <div className="text-danger">{errors.taskCurrentStatus}</div>
            )}
          </div>
          <div className="col-md-6 form-group">
            <FloatingLabelDropDown
              label={"Task with respect to "}
              onChange={onChange}
              isMandatory={true}
              value={data.taskRespect}
              name={"taskRespect"}
              optionsList={sortAlphabetically(taskRespects)}
            />
            {errors?.taskRespect && (
              <div className="text-danger">{errors.taskRespect}</div>
            )}
          </div>
          {data.taskRespect === TaskRespects.Department && (
            <div className="col-md-12 form-group">
              <FloatingLabelDropDown
                label={"Department "}
                onChange={onChange}
                isMandatory={true}
                value={data.departmentId}
                name={"departmentId"}
                optionsList={sortAlphabetically(departments)}
              />
              {errors?.departmentId && (
                <div className="text-danger">{errors.departmentId}</div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="modal-footer mb-4">
        <button type="button" className="btn btn-primary" onClick={onSave}>
          Save
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          data-bs-dismiss="modal"
          onClick={() => setShowCreate(false)}
        >
          Cancel
        </button>
      </div>
      <SweetAlert
        show={showAlert}
        success={false}
        type={alertType}
        title={alertTitle}
        onConfirm={() => onConfirm()}
        showCancel={showAlertCancel}
        onCancel={() => {
          setShowAlert(false);
          setShowAlertCancel(false);
        }}
        cancelBtnText={"No"}
        confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
      ></SweetAlert>
      {isLoading && <LoaderSpinner />}
    </>
  );
};

const mapDispatchToProps = {
  createTask: (data: any, callBack: Function) => createTask(data, callBack),
  getTaskRespects: () => getTaskRespects(),
  getTaskStatus: () => getTaskStatus(),
  getDepartments: () => getDepartments(),
};

function mapStateToProps(state: any) {
  return {
    departments: state.common.departments,
    taskStatus: state.common.taskStatus,
    taskRespects: state.common.taskRespects,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TaskCreate);
